/**
 * MessageThreadPage Component
 *
 * Individual message conversation view.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DashboardLayout, MessageBubble } from './components';
import { useMessageThread } from './hooks';
import { useQuickReplies } from '@/services/quickReplies';
import { useAuth } from '@/contexts';

export const MessageThreadPage: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const { thread, messages, isLoading, sendMessage } = useMessageThread(threadId);
  const { user } = useAuth();
  // Dashboard messages are guests messaging hosts (or vice versa). The viewer
  // role determines which chips show - fall back to 'guest' for safety.
  const viewerRole = user?.role === 'host' || user?.role === 'admin' ? 'host' : 'guest';
  const { replies: quickReplies } = useQuickReplies(viewerRole);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    try {
      await sendMessage(newMessage.trim());
      setNewMessage('');
    } catch (error: unknown) {
      // Cloud Function HttpsError carries the user-facing reason from the
      // moderation pipeline (e.g. "Phone numbers can't be shared...").
      const e = error as { code?: string; message?: string };
      console.error('Failed to send message:', error);
      toast.error(e.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-16 bg-gray-200 rounded-xl" />
          <div className="h-96 bg-gray-100 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (!thread) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <h2 className="text-xl font-medium text-gray-900 mb-2">Conversation not found</h2>
          <Link to="/dashboard/messages" className="text-primary-600 hover:underline">
            Back to messages
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col h-[calc(100vh-220px)] min-h-[500px] bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-4 p-4 border-b border-gray-200 bg-white">
          <Link
            to="/dashboard/messages"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </Link>

          <div className="flex items-center gap-3 flex-1 min-w-0">
            {thread.otherParticipant.photo ? (
              <img
                src={thread.otherParticipant.photo}
                alt={thread.otherParticipant.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 font-medium">
                {thread.otherParticipant.name.charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="font-semibold text-gray-900 truncate">
                {thread.otherParticipant.name}
              </h2>
              {thread.listing && (
                <Link
                  to={`/listing/${thread.listing.id}`}
                  className="text-sm text-gray-500 hover:text-primary-600 truncate block"
                >
                  {thread.listing.title}
                </Link>
              )}
            </div>
          </div>

          {thread.listing && (
            <Link
              to={`/listing/${thread.listing.id}`}
              className="hidden sm:block flex-shrink-0"
            >
              <img
                src={thread.listing.photo}
                alt={thread.listing.title}
                className="w-12 h-12 rounded-lg object-cover"
              />
            </Link>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {messages.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>Start a conversation with {thread.otherParticipant.name}</p>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isOwn={message.senderId === 'mock-user'}
                  senderName={
                    message.senderId === 'mock-user'
                      ? undefined
                      : thread.otherParticipant.name
                  }
                  senderPhoto={
                    message.senderId === 'mock-user'
                      ? undefined
                      : thread.otherParticipant.photo
                  }
                />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200 bg-white">
          {quickReplies.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {quickReplies.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setNewMessage(r.text)}
                  className="px-3 py-1.5 text-sm border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  {r.text}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-3">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              rows={1}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
            <button
              onClick={handleSend}
              disabled={!newMessage.trim() || isSending}
              className="px-4 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSending ? (
                <LoadingSpinner className="w-5 h-5" />
              ) : (
                <SendIcon className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            Tip: keep all communication on Darak. Sharing phone numbers, emails,
            or social handles isn't allowed.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Icons
const ChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const SendIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
  </svg>
);

const LoadingSpinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
      fill="none"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export default MessageThreadPage;
